import { usePunishmentLogic } from '@/hooks/usePunishmentLogic';
import { PunishmentForm } from '@/components/company/punishment/PunishmentForm';
import { NumberedSanctionList } from '@/components/company/punishment/NumberedSanctionList';
import FormModal from '@/components/company/FormModal';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { IPunishment, ISanction } from '@/states/punishment.state';

const stripHtml = (value: string | undefined): string => (value ? value.replace(/<[^>]*>/g, ' ').trim() : '');

const getSeverityBadgeClass = (severity: string): string => {
  switch (severity) {
    case 'SP3':
      return 'text-red-700 bg-red-100 border-red-300';
    case 'SP2':
      return 'text-yellow-700 bg-yellow-100 border-yellow-300';
    default:
      return 'text-green-700 bg-green-100 border-green-300';
  }
};

const defaultPunishmentTemplates: IPunishment[] = [
  {
    id: 'sp1-default',
    severity_level: 'SP1',
    definition_warning: 'Ringan, mengganggu operasional.',
    sanction_description: '3 Bulan.',
    sanctions: [],
  },
  {
    id: 'sp2-default',
    severity_level: 'SP2',
    definition_warning: 'Sedang, mengganggu kinerja & rugi material.',
    sanction_description: '3 Bulan.',
    sanctions: [],
  },
  {
    id: 'sp3-default',
    severity_level: 'SP3',
    definition_warning: 'Berat, merusak kinerja & terbukti merugikan.',
    sanction_description: 'Langsung dikeluarkan.',
    sanctions: [],
  },
];

export default function Punishment() {
  const isReadOnly = useIsReadOnly();
  const { isLoading, paginatedData, formControl, formMode, setFormMode, formRef, handleSave, onEdit, reset } =
    usePunishmentLogic();

  const handleCancel = () => {
    reset();
    setFormMode(null);
  };

  const mergedRows: IPunishment[] = defaultPunishmentTemplates.map(tpl => {
    const saved = paginatedData?.content?.find(item => item.severity_level === tpl.severity_level);
    return saved ?? tpl;
  });

  return (
    <div className="w-full">
      <FormModal
        isOpen={formMode !== null}
        onClose={handleCancel}
        title="Kelola Punishment"
        onSubmit={handleSave}
        submitLabel="Perbarui"
        hideSubmit={isReadOnly}
        panelClassName="w-full max-w-5xl"
      >
        <PunishmentForm formControl={formControl} formRef={formRef} handleSave={handleSave} disabled={isReadOnly} />
      </FormModal>

      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-[#1A1A1A]">
        {/* Description row */}
        <div className="px-5 py-3 bg-white dark:bg-[#1A1A1A] border-b border-gray-100 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Daftar sanksi administratif dan denda kas berdasarkan tingkat keparahan pelanggaran SP1, SP2, SP3.
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-[#111111] border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide w-32">
                  Jenis Peringatan
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                  Definisi Peringatan
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide w-48">
                  Masa Berlakunya Sanksi
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                  Bentuk/Tindakan Pelanggaran
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                  Sanksi / Denda
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center uppercase tracking-wide w-24">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                    Memuat data...
                  </td>
                </tr>
              ) : (
                mergedRows.map(row => (
                  <PunishmentRow
                    key={row.severity_level}
                    row={row}
                    isReadOnly={isReadOnly}
                    onEdit={() => onEdit(row)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface PunishmentRowProps {
  row: IPunishment;
  isReadOnly: boolean;
  onEdit: () => void;
}

function PunishmentRow({ row, isReadOnly, onEdit }: PunishmentRowProps) {
  const sanctions: ISanction[] = row.sanctions ?? [];
  const badgeClass = getSeverityBadgeClass(row.severity_level ?? '');

  const definitionText = stripHtml(row.definition_warning) || '-';
  const sanctionDescText = stripHtml(row.sanction_description) || '-';

  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-white/5 align-top">
      {/* Jenis Peringatan */}
      <td className="px-4 py-4">
        <span className={`inline-flex items-center px-3 py-0.5 rounded-md text-sm font-bold border ${badgeClass}`}>
          {row.severity_level ?? '-'}
        </span>
      </td>

      {/* Definisi Peringatan */}
      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-[200px]">{definitionText}</td>

      {/* Masa Berlakunya Sanksi */}
      <td className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{sanctionDescText}</td>

      {/* Bentuk/Tindakan Pelanggaran */}
      <td className="px-4 py-4">
        {sanctions.length === 0 ? (
          <span className="text-sm text-gray-400 dark:text-gray-500 italic">Belum diisi</span>
        ) : (
          <NumberedSanctionList items={sanctions.map(s => s.warning_clause)} />
        )}
      </td>

      {/* Sanksi / Denda */}
      <td className="px-4 py-4">
        {sanctions.length === 0 ? (
          <span className="text-sm text-gray-400 dark:text-gray-500 italic">Belum diisi</span>
        ) : (
          <NumberedSanctionList items={sanctions.map(s => s.fee)} />
        )}
      </td>

      {/* Aksi */}
      <td className="px-4 py-4 text-center">
        {!isReadOnly && (
          <button
            type="button"
            onClick={onEdit}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Kelola
          </button>
        )}
      </td>
    </tr>
  );
}
